import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ExpandableSection from "@/components/job/ExpandableSection";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import RequiredMark from "@/components/ui/required-mark";
import { TextInput } from "@/components/ui/textInput";
import { createEditJobOptionsQuery } from "@/hooks/useConfigOptions";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";
import { ConfirmJobDetails, type JobDetailsSchema } from "./ConfirmationDialog";

const jobDetailsSchema = z.object({
  job_number: z.string({ message: "Job number is required" }).refine((jobNumber) => /^[a-zA-Z0-9]+$/.test(jobNumber), {
    message: "Job number can only contain letters and numbers",
  }),
  customer_number: z
    .string({ message: "Customer number is required" })
    .min(1, { message: "Customer number is required" }),
  plant_name: z.string({ message: "Plant Name is required" }).min(1, { message: "Plant Name is required" }),
  service_name: z.string({ message: "Service is required" }).min(1, { message: "Service is required" }),
  design_code: z.string({ message: "Design code is required" }).min(1, { message: "Design code is required" }),
  branch: z.string({ message: "Branch is required" }).min(1, { message: "Branch is required" }),
});

const cardName = "jobDetails";

type MechanicalJobDetailsFormProps = {
  designCodesOptions: string[];
  savedData: JobDetailsSchema;
  onSubmit: (data: JobDetailsSchema) => void;
};

function MechanicalJobDetailsForm({ designCodesOptions, savedData, onSubmit }: MechanicalJobDetailsFormProps) {
  const form = useForm<JobDetailsSchema>({
    resolver: zodResolver(jobDetailsSchema),
    values: savedData,
  });
  const lastSavedDataRef = useRef<string | null>(null);

  useEffect(() => {
    const serialized = JSON.stringify(savedData);
    if (serialized !== lastSavedDataRef.current) {
      form.reset(savedData);
      lastSavedDataRef.current = serialized;
    }
  }, [form, savedData]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-wrap gap-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 *:min-w-0">
          <FormField
            control={form.control}
            name="job_number"
            render={({ field }) => (
              <FormItem className="flex flex-1 flex-col">
                <FormLabel className="text-left">
                  Job Number
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <TextInput placeholder="Job number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="customer_number"
            render={({ field }) => (
              <FormItem className="flex flex-1 flex-col">
                <FormLabel className="text-left">
                  Customer Number
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <TextInput placeholder="Customer number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="plant_name"
            render={({ field }) => (
              <FormItem className="flex flex-1 flex-col">
                <FormLabel className="text-left">
                  Plant Name
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <TextInput placeholder="Plant name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="service_name"
            render={({ field }) => (
              <FormItem className="flex flex-1 flex-col">
                <FormLabel className="text-left">
                  Service
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <TextInput placeholder="Service name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="design_code"
            control={form.control}
            render={({ field }) => {
              return (
                <FormItem className="flex flex-1 flex-col">
                  <FormLabel>
                    Design Code
                    <RequiredMark />
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue defaultValue={field.value} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {designCodesOptions.map((option) => {
                        return (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="branch"
            render={({ field }) => (
              <FormItem className="flex flex-1 flex-col">
                <FormLabel className="text-left">
                  Branch
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <TextInput placeholder="branch" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button className="w-full self-end md:w-fit" type="submit">
          Save
        </Button>
      </form>
    </Form>
  );
}

export function MechanicalJobDetails() {
  const [confirmData, setConfirmData] = useState<JobDetailsSchema | null>(null);

  const { sections, setJobDetails, toggleExpandableSection } = useCUMechanicalJobStore();
  const savedJobDetails = sections.jobDetails;

  const { data: designCodesOptions = [] } = useQuery({
    ...createEditJobOptionsQuery,
    select(response) {
      return (response.design_codes ?? []).map((code) => {
        if (typeof code === "string") {
          return code;
        }

        return code.name;
      });
    },
  });

  function handleSaveJobDetails() {
    setConfirmData(null);
    setJobDetails({ data: confirmData!, status: "done" });
  }

  function handleToggleSection() {
    toggleExpandableSection(cardName);
  }

  return (
    <>
      <ExpandableSection status={sections.jobDetails.status} setIsExpanded={handleToggleSection} title="Job Details">
        <MechanicalJobDetailsForm
          designCodesOptions={designCodesOptions}
          savedData={{
            job_number: savedJobDetails.data.job_number ?? "",
            customer_number: savedJobDetails.data.customer_number ?? "",
            plant_name: savedJobDetails.data.plant_name ? String(savedJobDetails.data.plant_name) : "",
            service_name: savedJobDetails.data.service_name ? String(savedJobDetails.data.service_name) : "",
            design_code: savedJobDetails.data.design_code ?? "",
            branch: savedJobDetails.data.branch ?? "",
          }}
          onSubmit={(data) => setConfirmData(data)}
        />
      </ExpandableSection>
      <ConfirmJobDetails
        confirmData={confirmData}
        handleSaveJobDetails={handleSaveJobDetails}
        setConfirmData={setConfirmData}
      />
    </>
  );
}
